import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid,
	IconButton,
	InputAdornment,
	Snackbar,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AppLayout from "../../components/AppLayout";
import { useUser } from "../../context/UserContext";
import { AuthService } from "../../service/auth_service";
import SuaConta from "./SuaConta";
import TermosUso from "./TermosUso";

// Mesmas abas do Configurações do Campo: "Sua conta" é a empresa; os dados de
// acesso do usuário ficam em "Login e privacidade".
const ABA_CONTA = 0;
const ABA_LOGIN = 1;
const ABA_TERMOS = 2;

const SENHA_MIN_LENGTH = 6;

const fieldSx = {
	"& .MuiOutlinedInput-root": {
		"& fieldset": { borderColor: "#e0e0e0" },
		"&:hover fieldset": { borderColor: "#1a9e8f" },
		"&.Mui-focused fieldset": { borderColor: "#1a9e8f" },
	},
};

const cardSx = {
	bgcolor: "white",
	borderRadius: "8px",
	boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
	p: { xs: 2.5, md: 4 },
	mb: 3,
};

const titleSx = {
	color: "#1a9e8f",
	fontWeight: 600,
	mb: 3,
	fontSize: "17px",
};

const buttonSx = {
	bgcolor: "#1a9e8f",
	textTransform: "none",
	fontSize: "15px",
	px: 3,
	"&:hover": { bgcolor: "#158f80" },
};

const SENHA_VAZIA = { senhaAtual: "", novaSenha: "", confirmacao: "" };

function Configuracoes() {
	const { refreshUser } = useUser();

	const [abaAtiva, setAbaAtiva] = useState(ABA_CONTA);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState("");

	const [dados, setDados] = useState({ nome: "", email: "", login: "" });
	const [dadosError, setDadosError] = useState("");
	const [salvandoDados, setSalvandoDados] = useState(false);

	const [senha, setSenha] = useState(SENHA_VAZIA);
	const [senhaError, setSenhaError] = useState("");
	const [salvandoSenha, setSalvandoSenha] = useState(false);
	const [senhaVisivel, setSenhaVisivel] = useState(false);

	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	const initData = useCallback(async () => {
		setLoading(true);
		try {
			const data = await new AuthService().getAccount();
			setDados({
				nome: data.nome || "",
				email: data.email || "",
				login: data.login || "",
			});
			setLoadError("");
		} catch (error) {
			setLoadError(error.message || "Erro ao carregar os dados da conta.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		initData();
	}, [initData]);

	const handleDadosChange = (field, value) => {
		setDados((prev) => ({ ...prev, [field]: value }));
	};

	const handleSenhaChange = (field, value) => {
		setSenha((prev) => ({ ...prev, [field]: value }));
	};

	const validarDados = () => {
		const nome = dados.nome.trim();
		const email = dados.email.trim();
		const login = dados.login.trim();

		if (!nome) return "Informe o nome.";
		if (!email) return "Informe o e-mail.";
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Informe um e-mail válido.";
		if (!login) return "Informe o login.";
		if (login.length < 3) return "O login deve ter ao menos 3 caracteres.";
		if (/\s/.test(login)) return "O login não pode conter espaços.";

		return "";
	};

	const handleSalvarDados = async () => {
		const erro = validarDados();
		if (erro) {
			setDadosError(erro);
			return;
		}

		setDadosError("");
		setSalvandoDados(true);
		try {
			await new AuthService().updateAccount({
				nome: dados.nome.trim(),
				email: dados.email.trim(),
				login: dados.login.trim(),
			});

			// O nome aparece no avatar/menu, que lê do UserContext.
			await refreshUser();
			setSnackbar({ open: true, message: "Dados atualizados com sucesso!", severity: "success" });
		} catch (error) {
			setDadosError(error.message || "Erro ao salvar os dados da conta.");
		} finally {
			setSalvandoDados(false);
		}
	};

	const validarSenha = () => {
		if (!senha.senhaAtual) return "Informe a senha atual.";
		if (!senha.novaSenha) return "Informe a nova senha.";
		if (senha.novaSenha.length < SENHA_MIN_LENGTH)
			return `A nova senha deve ter ao menos ${SENHA_MIN_LENGTH} caracteres.`;
		if (senha.novaSenha === senha.senhaAtual) return "A nova senha deve ser diferente da atual.";
		if (senha.novaSenha !== senha.confirmacao) return "A confirmação não confere com a nova senha.";

		return "";
	};

	const handleAlterarSenha = async () => {
		const erro = validarSenha();
		if (erro) {
			setSenhaError(erro);
			return;
		}

		setSenhaError("");
		setSalvandoSenha(true);
		try {
			await new AuthService().changePassword(senha.senhaAtual, senha.novaSenha);

			setSenha(SENHA_VAZIA);
			setSnackbar({ open: true, message: "Senha alterada com sucesso!", severity: "success" });
		} catch (error) {
			setSenhaError(error.message || "Erro ao alterar a senha.");
		} finally {
			setSalvandoSenha(false);
		}
	};

	const senhaAdornment = (
		<InputAdornment position="end">
			<IconButton
				onClick={() => setSenhaVisivel((prev) => !prev)}
				edge="end"
				aria-label={senhaVisivel ? "Ocultar senhas" : "Mostrar senhas"}
				sx={{ color: "#1a9e8f" }}
			>
				{senhaVisivel ? <VisibilityOffIcon /> : <VisibilityIcon />}
			</IconButton>
		</InputAdornment>
	);

	const renderConteudo = () => {
		if (loading) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress sx={{ color: "#1a9e8f" }} />
				</Box>
			);
		}

		if (loadError) {
			return (
				<Alert
					severity="error"
					action={
						<Button color="inherit" size="small" onClick={initData}>
							Tentar novamente
						</Button>
					}
				>
					{loadError}
				</Alert>
			);
		}

		return (
			<>
				<Box sx={cardSx}>
					<Typography variant="subtitle1" sx={titleSx}>
						Dados de acesso
					</Typography>

					{dadosError && (
						<Alert severity="error" sx={{ mb: 3 }}>
							{dadosError}
						</Alert>
					)}

					<Grid container spacing={3}>
						<Grid size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								label="Nome *"
								value={dados.nome}
								onChange={(e) => handleDadosChange("nome", e.target.value)}
								placeholder="Digite seu nome completo"
								sx={fieldSx}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								label="E-mail *"
								type="email"
								value={dados.email}
								onChange={(e) => handleDadosChange("email", e.target.value)}
								placeholder="exemplo@email.com"
								sx={fieldSx}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								label="Login *"
								value={dados.login}
								onChange={(e) => handleDadosChange("login", e.target.value)}
								placeholder="Digite seu login de acesso"
								helperText="É com este login que você entra no sistema."
								sx={fieldSx}
							/>
						</Grid>
					</Grid>

					<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
						<Button
							variant="contained"
							onClick={handleSalvarDados}
							disabled={salvandoDados}
							sx={buttonSx}
						>
							{salvandoDados ? "Salvando..." : "Salvar alterações"}
						</Button>
					</Box>
				</Box>

				<Box sx={cardSx}>
					<Typography variant="subtitle1" sx={titleSx}>
						Alterar senha
					</Typography>

					{senhaError && (
						<Alert severity="error" sx={{ mb: 3 }}>
							{senhaError}
						</Alert>
					)}

					<Grid container spacing={3}>
						<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								fullWidth
								label="Senha atual *"
								type={senhaVisivel ? "text" : "password"}
								value={senha.senhaAtual}
								onChange={(e) => handleSenhaChange("senhaAtual", e.target.value)}
								autoComplete="current-password"
								slotProps={{ input: { endAdornment: senhaAdornment } }}
								sx={fieldSx}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								fullWidth
								label="Nova senha *"
								type={senhaVisivel ? "text" : "password"}
								value={senha.novaSenha}
								onChange={(e) => handleSenhaChange("novaSenha", e.target.value)}
								autoComplete="new-password"
								helperText={`Mínimo de ${SENHA_MIN_LENGTH} caracteres.`}
								sx={fieldSx}
							/>
						</Grid>

						<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								fullWidth
								label="Confirmar nova senha *"
								type={senhaVisivel ? "text" : "password"}
								value={senha.confirmacao}
								onChange={(e) => handleSenhaChange("confirmacao", e.target.value)}
								autoComplete="new-password"
								sx={fieldSx}
							/>
						</Grid>
					</Grid>

					<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
						<Button
							variant="contained"
							onClick={handleAlterarSenha}
							disabled={salvandoSenha}
							sx={buttonSx}
						>
							{salvandoSenha ? "Alterando..." : "Alterar senha"}
						</Button>
					</Box>
				</Box>
			</>
		);
	};

	return (
		<AppLayout currentPage="configuracoes">
			<Box sx={{ maxWidth: 900, mx: "auto" }}>
				<Box sx={{ mb: 3 }}>
					<Typography
						variant="h5"
						sx={{ color: "#333", fontWeight: 600, fontSize: "22px", mb: 0.5 }}
					>
						Configurações
					</Typography>
					<Typography sx={{ color: "#777", fontSize: "14px" }}>
						Dados da empresa, acesso e termos de uso
					</Typography>
				</Box>

				<Tabs
					value={abaAtiva}
					onChange={(_, aba) => setAbaAtiva(aba)}
					variant="scrollable"
					sx={{
						mb: 3,
						bgcolor: "white",
						borderRadius: "12px",
						border: "1px solid #dfe3e8",
						"& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
						"& .Mui-selected": { color: "#1a9e8f" },
						"& .MuiTabs-indicator": { bgcolor: "#1a9e8f" },
					}}
				>
					<Tab
						icon={<AccountCircleOutlinedIcon sx={{ fontSize: 18 }} />}
						iconPosition="start"
						label="Sua conta"
						value={ABA_CONTA}
					/>
					<Tab
						icon={<LockOpenIcon sx={{ fontSize: 18 }} />}
						iconPosition="start"
						label="Login e privacidade"
						value={ABA_LOGIN}
					/>
					<Tab
						icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />}
						iconPosition="start"
						label="Termos de uso"
						value={ABA_TERMOS}
					/>
				</Tabs>

				{abaAtiva === ABA_CONTA && (
					<SuaConta
						cardSx={cardSx}
						titleSx={titleSx}
						fieldSx={fieldSx}
						buttonSx={buttonSx}
						onNotify={(message) => setSnackbar({ open: true, message, severity: "success" })}
					/>
				)}
				{abaAtiva === ABA_LOGIN && renderConteudo()}
				{abaAtiva === ABA_TERMOS && <TermosUso cardSx={cardSx} titleSx={titleSx} />}
			</Box>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
					sx={{ width: "100%" }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</AppLayout>
	);
}

export default Configuracoes;
